import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import type { BancoConvenioRegras } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { formatBackendErr } from "../../lib/format-backend-err";

/**
 * Regras de integracao por convenio — a razao de esta documentacao viver dentro
 * do produto e nao num site aberto.
 *
 * Cada prefeitura negocia as suas: teto de parcelas, teto de juros, exigencia de
 * CCB. Num site publico um banco leria as condicoes que o outro tem no mesmo
 * convenio. Aqui a tela sabe quem esta lendo e mostra so o que e dele.
 *
 * A separacao entre VALIDADO e INFORMATIVO e o ponto central: `idadeMin`,
 * `vinculosAceitos`, `maxComprometimentoPct` e `exigeBanco2FA` sao configuraveis
 * pela averbadora mas nao tem ponto de validacao nenhum no backend. Mostrar tudo
 * junto faria o parceiro implementar barreira que a plataforma nao aplica — e
 * descobrir isso em producao.
 */
export function RegrasConvenio() {
  const q = useQuery({
    queryKey: ["banco", "convenio-regras"],
    queryFn: () => atlas.banco.convenioRegras(),
    staleTime: 60_000,
  });

  // `/banco/convenios/regras#CONV-…` (link do card em Convênios): rola até o
  // convenio pedido quando os cards existirem — eles chegam depois da consulta.
  useEffect(() => {
    if (!q.data) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView({ block: "start" });
  }, [q.data]);

  if (q.isPending) return <p style={{ color: "var(--text-muted)" }}>Carregando as regras dos seus convênios…</p>;
  if (q.isError) {
    return <p style={{ color: "var(--danger-500)" }}>{formatBackendErr(q.error, "Falha ao consultar os convênios.")}</p>;
  }

  const convenios = q.data?.convenios ?? [];
  if (convenios.length === 0) {
    return (
      <div style={{ fontSize: 14, color: "var(--text-muted)" }}>
        <h1 style={{ fontSize: "1.6rem", color: "var(--text)", margin: "0 0 10px" }}>Regras por convênio</h1>
        <p>Sua consignatária ainda não tem convênio ativo. Assim que a averbadora vincular uma organização, as regras dela aparecem aqui.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 820 }}>
      <div>
        <h1 style={{ fontSize: "1.6rem", margin: "0 0 10px", letterSpacing: "-0.02em" }}>Regras por convênio</h1>
        <p style={{ color: "var(--text-muted)", margin: 0, lineHeight: 1.7 }}>
          Cada órgão define as próprias condições, e elas valem para toda operação que você enviar
          naquele convênio. As mesmas regras saem pela API em{" "}
          <code>GET /v1/external/banco/convenios</code>, com os mesmos nomes de campo — dá para validar do seu
          lado antes de mandar uma proposta que seria recusada.
        </p>
      </div>

      {convenios.map((cv) => (
        <CardConvenio key={cv.id} cv={cv} />
      ))}

      <section style={{ fontSize: 13, color: "var(--text-muted)", borderTop: "1px solid var(--border)", paddingTop: 14, lineHeight: 1.7 }}>
        <b style={{ color: "var(--text)" }}>Sobre a folha.</b> A folha é aberta por <b>organização</b>, mas o dia de
        corte e o de repasse vêm do convênio que a abriu primeiro naquela competência. Se duas consignatárias operam na mesma
        organização com cortes diferentes, existe uma folha só — com o corte de quem chegou antes. Considere o corte
        como uma característica da organização, não do seu convênio.
      </section>
    </div>
  );
}

function CardConvenio({ cv }: { cv: BancoConvenioRegras }) {
  const v = cv.validadas;
  const i = cv.informativas;

  return (
    <article id={cv.id} style={{ border: "1px solid var(--border-strong)", borderRadius: 12, overflow: "hidden", scrollMarginTop: 80 }}>
      <header style={{ padding: "14px 16px", background: "var(--bg-elev)", borderBottom: "1px solid var(--border)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <b style={{ fontSize: 15 }}>{cv.nome}</b>
          <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>{cv.id}</code>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
          {cv.prefeitura} · {cv.uf} · verba <code>{cv.codigo_verba}</code> · corte dia {cv.data_corte} · repasse dia {cv.dia_repasse}
        </div>
      </header>

      {!cv.configurado ? (
        <div style={{
          margin: 16, padding: "10px 12px", borderRadius: 8, fontSize: 13, lineHeight: 1.6,
          border: "1px solid var(--gold-500)", background: "color-mix(in srgb, var(--gold-500) 12%, transparent)",
        }}>
          <b>Convênio ainda não parametrizado.</b> A averbadora não definiu as regras deste convênio, então{" "}
          <b>não há teto de parcelas nem de juros</b> sendo aplicado — a plataforma só barra pelo limite geral de{" "}
          {v.max_parcelas_plataforma} parcelas. Não implemente limites presumidos: confirme com a averbadora antes
          de operar.
        </div>
      ) : null}

      <Bloco
        titulo="Validado pela API"
        legenda="A operação é recusada se violar qualquer um destes."
        cor="var(--emerald-500)"
        linhas={[
          ["Prazo máximo", v.max_parcelas == null ? null : `${v.max_parcelas} parcelas`, `teto da plataforma: ${v.max_parcelas_plataforma}`],
          ["Teto de CET", v.taxa_max_am_pct == null ? null : `${v.taxa_max_am_pct}% a.m.`, v.taxa_max_am_pct == null ? "sem teto definido" : `envie como fração: ${(v.taxa_max_am_pct / 100).toFixed(4)}`],
          ["CCB assinada", v.exige_ccb ? "obrigatória para aprovar" : "não exigida", v.exige_ccb ? "anexe em POST /ccb/upload antes de aprovar" : ""],
          ["Senha de averbação", v.exige_senha_averbacao ? "exigida do servidor" : "não exigida", ""],
          ["Senha na proposta", v.exige_senha_proposta ? "exigida do servidor" : "não exigida", ""],
          ["Trava de margem", v.prazo_trava_horas == null ? null : `${v.prazo_trava_horas} h`, "pré-reserva expira e libera a margem"],
          ["Trava de portabilidade", v.prazo_portabilidade_du == null ? null : `${v.prazo_portabilidade_du} dias úteis`, ""],
          ["Vigência", v.vigencia_inicio ? `${v.vigencia_inicio}${v.vigencia_fim ? ` até ${v.vigencia_fim}` : " (sem fim)"}` : null, ""],
          ["Aceitando operações", v.config_ativo == null ? null : v.config_ativo ? "sim" : "não", ""],
        ]}
      />

      <Bloco
        titulo="Informativo — NÃO validado"
        legenda="A averbadora configura, mas a API não barra. Não implemente como regra."
        cor="var(--gold-500)"
        linhas={[
          ["Idade mínima", i.idade_min == null ? null : `${i.idade_min} anos`, ""],
          ["Idade máxima", i.idade_max == null ? null : `${i.idade_max} anos`, ""],
          ["Vínculos aceitos", i.vinculos_aceitos?.length ? i.vinculos_aceitos.join(", ") : null, ""],
          ["Comprometimento máx.", i.max_comprometimento_pct == null ? null : `${(i.max_comprometimento_pct * 100).toFixed(0)}%`, "a margem real usa 35% empréstimo / 5% cartão"],
          ["2FA da consignatária", i.exige_banco_2fa ? "marcado" : "não marcado", "sinalizador sem efeito na API"],
        ]}
      />

      {cv.regras_especiais ? (
        <section style={{ padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>
            Regras especiais desta organização
          </span>
          <p style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{cv.regras_especiais}</p>
        </section>
      ) : null}

      {cv.id_unico ? (
        <section style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", fontSize: 13, color: "var(--text-muted)" }}>
          <b style={{ color: "var(--text)" }}>Identificadores.</b> As operações desta prefeitura recebem id no formato{" "}
          <code>{cv.id_unico.formato}</code> com prefixo <code>{cv.id_unico.prefixo}</code> — por exemplo{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>{cv.id_unico.exemplo}</code>.
        </section>
      ) : null}
    </article>
  );
}

/** Uma tabela de regras. `null` no valor vira "não definido", nunca um padrão. */
function Bloco({ titulo, legenda, cor, linhas }: {
  titulo: string;
  legenda: string;
  cor: string;
  linhas: [string, string | null, string][];
}) {
  return (
    <section style={{ padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: cor, textTransform: "uppercase", letterSpacing: ".06em" }}>
          {titulo}
        </span>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{legenda}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(150px, auto) 1fr", gap: "6px 16px", fontSize: 13 }}>
        {linhas.map(([rotulo, valor, nota]) => (
          <Linha key={rotulo} rotulo={rotulo} valor={valor} nota={nota} />
        ))}
      </div>
    </section>
  );
}

function Linha({ rotulo, valor, nota }: { rotulo: string; valor: string | null; nota: string }) {
  return (
    <>
      <span style={{ color: "var(--text-muted)" }}>{rotulo}</span>
      <span>
        {valor == null ? (
          <i style={{ color: "var(--text-dim)" }}>não definido</i>
        ) : (
          <b style={{ fontWeight: 600 }}>{valor}</b>
        )}
        {nota ? <span style={{ color: "var(--text-dim)", marginLeft: 8, fontSize: 12 }}>{nota}</span> : null}
      </span>
    </>
  );
}
